import { styled } from '@linaria/react'
import React, { FC } from 'react'
import { FormattedMessage } from 'react-intl'
import type { BoxDto as TypeBox } from '../types/generated'
import { Typography } from '../ui/Typography'
import { colors } from '../styles/colors'
import { Box } from './Box'
import { TransactionLink } from './TransactionLink'
import { formatDateTime, getDateLocale } from '../shared/utils/dateFormat'
import { useAppContext } from '../contexts/App.context'
import { useBoxStatus } from '../hooks/useBoxStatus'
import { useClaimReward } from '../hooks/useClaimReward'
import { ButtonRegular } from '../ui/Button'
import { useNavigate } from 'react-router'
import { indexPagePath } from '../pages/IndexPage'

interface BoxDetailsProps {
  box: TypeBox
}

export const BoxDetails: FC<BoxDetailsProps> = ({ box }) => {
  const navigate = useNavigate()
  const { language } = useAppContext()

  const formatTimestamp = (timestamp: string) => {
    return formatDateTime(timestamp, getDateLocale(language))
  }

  const boxStatus = useBoxStatus(box)

  const { isClaimable, claimReward } = useClaimReward({
    box: box,
    recommendedVoteGas: box.recommendedVoteGas,
  })

  return (
    <>
      <GrayOverflow />

      <Container>
        <Box box={box} isSingleBox />

        <DetailsSection>
          <Typography fontSize={13} fontWeight={700} color={colors.secondary}>
            <FormattedMessage
              id="dialog.createBoxConfirm.details"
              defaultMessage="Детали"
            />
          </Typography>

          <DetailsList>
            <DetailItem>
              <Typography
                fontSize={12}
                fontWeight={400}
                color={colors.grey700}
                font="roboto"
              >
                <FormattedMessage id="box.creator" defaultMessage="Создатель" />
              </Typography>

              <DetailValue>
                <TransactionLink transactionId={box.owner} />
              </DetailValue>
            </DetailItem>

            <DetailItem>
              <Typography
                fontSize={12}
                fontWeight={400}
                color={colors.grey700}
                font="roboto"
              >
                <FormattedMessage id="box.contract" defaultMessage="Контракт" />
              </Typography>

              <DetailValue>
                <TransactionLink transactionId={box.address} />
              </DetailValue>
            </DetailItem>

            <DetailItem>
              <Typography
                fontSize={12}
                fontWeight={400}
                color={colors.grey700}
                font="roboto"
              >
                <FormattedMessage
                  id="box.createdDate"
                  defaultMessage="Дата создания"
                />
              </Typography>

              <DetailValue>
                <Typography
                  fontSize={12}
                  fontWeight={400}
                  color={colors.grey700}
                  font="roboto"
                >
                  {formatTimestamp(box.startTime)}
                </Typography>
              </DetailValue>
            </DetailItem>

            <DetailItem>
              <Typography
                fontSize={12}
                fontWeight={400}
                color={colors.grey700}
                font="roboto"
              >
                <FormattedMessage
                  id="box.endTime"
                  defaultMessage="Дата завершения"
                />
              </Typography>

              <DetailValue>
                <Typography
                  fontSize={12}
                  fontWeight={400}
                  color={colors.grey700}
                  font="roboto"
                >
                  {formatTimestamp(box.endTime)}
                </Typography>
              </DetailValue>
            </DetailItem>
          </DetailsList>
        </DetailsSection>

        {boxStatus.isFinish && (
          <ActionButtonContainer>
            {isClaimable ? (
              <ButtonRegular
                onClick={claimReward}
                backgroundColor={colors.secondary}
                textColor={colors.white}
              >
                <FormattedMessage
                  id="box.status.takeWinnings"
                  defaultMessage="Забрать вознаграждение"
                />
              </ButtonRegular>
            ) : (
              <ButtonRegular
                onClick={() => navigate(indexPagePath)}
                backgroundColor={colors.secondary}
                textColor={colors.white}
                fontSize={16}
              >
                <FormattedMessage
                  id="other-boxes"
                  defaultMessage="Другие боксы"
                />
              </ButtonRegular>
            )}
          </ActionButtonContainer>
        )}
      </Container>
    </>
  )
}

const bgColor = colors.bgSecondary

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  background: ${bgColor};
  padding-top: 12px;
  padding-bottom: 16px;
  z-index: 1;
`

const GrayOverflow = styled.div`
  position: fixed;
  background: ${bgColor};
  inset: 0;
`

const DetailsSection = styled.div`
  display: grid;
  gap: 16px;
  background: ${colors.white};
  border: 1px solid ${colors.grey200};
  border-radius: 24px;
  padding: 16px;
`

const DetailsList = styled.div`
  display: grid;
  gap: 8px;
`

const DetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const DetailValue = styled.div``

const ACTION_BUTTON_CONTAINER_BOTTOM_PADDING = '10px'

const ActionButtonContainer = styled.div`
  position: fixed;
  bottom: calc(
    var(--bottom-navigation-height) + ${ACTION_BUTTON_CONTAINER_BOTTOM_PADDING}
  );
  left: 0;
  right: 0;
  padding: 16px;
  display: grid;
`
